<template>
  <div class="container m-t-lg">
    <div class="columns">
      <div class="column content">
        <template v-if="$route.name !== 'admin-find-drafts'">
          <h2>Properties Dashboard</h2>
          <p>
            Here you may view and manage proprietors certification submissions.
          </p>
        </template>
        <template v-else>
          <h2>Drafts</h2>
          <p>
            Here you my view and manage "Draft" certifications submitted by an
            administrator.
          </p>
        </template>

        <div class="columns">
          <div class="column">
            <form @submit.prevent="triggerSearch">
              <label
                for="search-control"
                class="label has-text-weight-bold"
              >
                Search by:
              </label>
              <div class="field">
                <div class="control">
                  <search
                    id="search-control"
                    v-model="search"
                    :dropdown="searchTypes"
                    :attrs="{
                      input: {
                        placeholder: 'Search by...',
                        id: 'search-control',
                      },
                    }"
                    :doing-search="doingSearch"
                    @clear="clearSearchTable"
                    @search="triggerSearch"
                  />
                </div>
              </div>

              <template v-if="$route.name === 'admin-home'">
                <p class="label">
                  Refine your search.
                </p>
                <div class="field is-grouped is-block-mobile m-b-none">
                  <div class="control is-expanded">
                    <dropdown
                      v-model="status"
                      :default-option="`- Status - `"
                      name="status"
                      :options="statusOptions"
                    />
                  </div>
                  <div class="control is-expanded">
                    <dropdown
                      v-model="propertyType"
                      :default-option="`- Type - `"
                      name="propertyType"
                      :options="{
                        Rental: 'Residential',
                        Daycare: 'Family Daycare',
                      }"
                    />
                  </div>
                  <div class="control is-expanded">
                    <label for="">
                      <datepicker
                        :key="disableType"
                        v-model="startDate"
                        :disabled-dates="disabledDates"
                        name="startDate"
                        placeholder="Start date"
                        input-class="input"
                      />
                    </label>
                  </div>
                  <div class="control is-expanded">
                    <label for="">
                      <datepicker
                        :key="disableType"
                        v-model="endDate"
                        :disabled-dates="disabledDates"
                        name="endDate"
                        placeholder="End date"
                        input-class="input"
                      />
                    </label>
                  </div>
                  <div class="control">
                    <button
                      :disabled="doingSearch"
                      :class="{ 'is-loading': doingSearch }"
                      class="button search-button"
                    >
                      <span>Search</span>
                      <span class="icon is-small">
                        <i
                          class="fas fa-search"
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  </div>
                  <div class="control">
                    <button
                      :disabled="doingSearch"
                      class="button is-light clear-button"
                      title="Clear"
                      @click.prevent.stop="reset"
                    >
                      <!-- <span>Clear</span> -->
                      <span class="icon is-small">
                        <i
                          class="fas fa-eraser"
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  </div>
                </div>
              </template>
            </form>
            <p v-if="$route.name === 'admin-home'">
              <small>
                You can narrow down your search by <strong>Status</strong>, and
                Submitted At date range using <strong>Start date</strong> and
                <strong>End date</strong>
              </small>
            </p>
          </div>
        </div>

        <div class="columns is-vcentered">
          <div class="column">
            <h3 class="m-none">
              Certifications
            </h3>
          </div>
          <div
            v-if="$route.name === 'admin-find-drafts'"
            class="column has-text-right"
          >
            <button
              class="button is-primary"
              @click.prevent="$refs['new-submission'].show()"
            >
              New Certification
            </button>
          </div>
        </div>
        <div
          class="results-table-wrapper"
          :class="{ 'hide-edges': hideEdges }"
          @click.capture="handleTableClick"
        >
          <div
            v-if="doingSearch"
            class="table-loading-overlay"
            aria-live="polite"
          >
            <div class="searching-row">
              Searching results...
            </div>
          </div>

          <v-server-table
            ref="dashboard-table"
            :columns="tableColumns"
            :options="tableOptions"
            @loaded="loaded"
          >
            <template
              slot="certificationStatus"
              slot-scope="props"
            >
              <status-tag :status="props.row.certificationStatus" />
            </template>

            <template
              slot="submittedAt"
              slot-scope="props"
            >
              {{
                $dayjs
                  .utc(props.row.submittedAt)
                  .local()
                  .format(tableOptions.dateFormat)
              }}
            </template>

            <template
              slot="submitter"
              slot-scope="props"
            >
              {{ props.row.submitter.firstName }}
              {{ props.row.submitter.lastName }}
            </template>

            <template
              slot="propertyType"
              slot-scope="props"
            >
              <span
                v-if="props.row.propertyType === 'Daycare'"
                class="icon"
              >
                <i
                  class="fad fa-child"
                  aria-hidden="true"
                />
              </span>
              <span
                v-else
                class="icon"
              >
                <i
                  class="fad fa-car-building"
                  aria-hidden="true"
                />
              </span>
            </template>

            <template
              slot="actions"
              slot-scope="props"
            >
              <a
                class="button is-small is-secondary open-button"
                :target="vulcanEnv !== 'test' ? '_blank' : '_self'"
                :href="
                  $router.resolve({
                    name: 'submissions-details',
                    params: { uuid: props.row.id },
                  }).href
                "
              >
                <span>Open</span>
              </a>
              <button
                class="button is-small is-outlined is-danger m-l-sm"
                title="Remove submission"
                @click="withdrawCertification(props.row)"
              >
                <span
                  class="icon"
                  aria-hidden="true"
                >
                  <i class="fas fa-trash" />
                </span>
              </button>
            </template>
          </v-server-table>
        </div>
      </div>
    </div>

    <!-- New submission modal -->
    <new-submission ref="new-submission" />
  </div>
</template>

<script>
import Vue from "vue";
import Search from "@/components/common/Search";
import { ServerTable } from "vue-tables-2";
import { options } from "@/config/tableOptions";
import Datepicker from "vuejs-datepicker";
import StatusTag from "@/components/common/StatusTag";
import NewSubmission from "@/components/NewSubmission";
import Searches from "@/mixins/searches";

Vue.use(ServerTable, {}, false, "bulma");

export default {
  name: "SelectProperty",
  components: {
    Search,
    Datepicker,
    StatusTag,
    NewSubmission,
  },
  mixins: [ Searches ],
  data() {
    return {
      searchAction: "properties/getEligibilityChecks",
      limit: 15,
      statusOptions: {
        Pending: "Pending",
        Failed: "Failed",
        Revoked: "Rejected",
        Passed: "Passed",
        Expired: "Expired",
        Expiring: "Expiring",
      },
      tableOptions: options({
        requestFunction: this.requestFunction,
        responseAdapter: this.responseAdapter,
        perPage: 15,
        dateColumns: [ "submittedAt" ],
        sortable: [ "submittedAt" ],
        orderBy: {
          ascending: false,
          column: "submittedAt",
        },
        filterable: [],
        resizableColumns: true,
        columnsClasses: {
          submittedAt: "nowrap",
        },
      }),
      tableColumns: [
        "opaAccountNumber",
        "opaAddress",
        "submitter",
        "numberOfUnits",
        "submittedAt",
        "propertyType",
        "certificationStatus",
        "actions",
      ],
      returnFromSearchParam: "eligibilityChecks",
      searchTypes: { opaAccountNumber: "Address", zipCode: "Zip code" },
      propertyType: null,
      disableType: "to",
      disabledDates: {
        from: new Date(),
      },
      skipNextLoadedSearchReset: false,

      orderBy: "submittedAt",
    };
  },
  watch: {
    status(newStatus, oldStatus) {
      if (
        oldStatus === "Expiring" ||
        (newStatus === "Expiring" && !!oldStatus)
      ) {
        this.startDate = null;
        this.endDate = null;
      }
      this.updateDisabledDates(newStatus);
    },
  },
  methods: {
    handleTableClick(event) {
      const target = event && event.target;
      if (!target || typeof target.closest !== "function") {
        return;
      }

      const paginationControl = target.closest(".VuePagination a, .VuePagination button");
      if (!paginationControl) {
        return;
      }

      const parentLi = paginationControl.closest("li");
      const isDisabled =
        paginationControl.classList.contains("disabled") ||
        paginationControl.getAttribute("aria-disabled") === "true" ||
        (parentLi && parentLi.classList.contains("disabled"));

      if (!isDisabled) {
        this.doingSearch = true;
      }
    },

    async waitForLoadingPaint() {
      await this.$nextTick();
      await new Promise((resolve) => {
        if (typeof window !== "undefined" && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => resolve());
          return;
        }

        setTimeout(resolve, 0);
      });
    },

    responseAdapter(data) {
      const resp = { data: [], count: 0 };
      if (typeof data === "object" && data.totalCount !== undefined) {
        resp.data = data[this.returnFromSearchParam] || [];
        resp.count = data.totalCount || 0;

        if (data.pageNumber > data.totalPages) {
          resp.data = [];
          resp.count = 0;
          this.page = 1;
          this.triggerSearch();
        }
      }

      return resp;
    },

    loaded(data) {
      if (this.skipNextLoadedSearchReset) {
        this.skipNextLoadedSearchReset = false;
      } else {
        this.doingSearch = false;
      }

      this.totalCount = data.totalCount;
    },

    async requestFunction(params) {
      if (this.requestMadeByAnUser === undefined || this.requestMadeByAnUser === true) {
        this.doingSearch = true;

        let query = this.$route.query;
        query.ascending = query.ascending === "true";

        const pageNumber = parseInt(query.pageNumber || params.page || this.page || 1);
        const pageSize = parseInt(this.limit || query.pageSize || params.limit || 15);

        let requestObject = {
          ...params,
          ...query,
          pageNumber,
          pageSize,
        };

        requestObject.orderByCol = query.orderByCol || params.orderBy;

        if (this.$route.name === "admin-find-drafts") {
          requestObject.status = "Draft";
        }

        this.requestMadeByAnUser = false;
        this.lastLoadedData = await this.$store.dispatch(this.searchAction, requestObject);
        return this.lastLoadedData;
      }

      this.requestMadeByAnUser = false;
      this.skipNextLoadedSearchReset = this.doingSearch;
      return this.lastLoadedData;
    },

    async triggerSearch() {
      this.doingSearch = true;
      await this.waitForLoadingPaint();

      let startDate = this.$dayjs(this.startDate).format("YYYY-MM-DD");
      let endDate = this.$dayjs(this.endDate).format("YYYY-MM-DD");

      this.customObject = {
        ...this.customObject,
        ...{
          opaAccountNumber: undefined,
          zipCode: undefined,
          startDate: startDate !== "Invalid Date" ? startDate : undefined,
          endDate: endDate !== "Invalid Date" ? endDate : undefined,
          status: this.status || undefined,
          pageNumber: parseInt(this.page),
          pageSize: parseInt(this.limit),
          orderByCol: this.orderBy,
          ascending: this.ascending ? true : false,
          type: this.search.type,
          search: !this.search.search ? undefined : this.search.search,
          propertyType: !this.propertyType ? undefined : this.propertyType,
          caseType: !this.caseType ? undefined : this.caseType,
        },
      };

      Object.keys(this.searchTypes).forEach(element => this.customObject[element] = undefined);

      if (this.search.type === "opaAccountNumber" && !!this.search.search) {
        try {
          this.customObject[this.search.type] = await this.$store.dispatch("properties/getOPAAccountNumbers", this.search.search);
        } catch (error) {
          this.doingSearch = false;
          throw error;
        }
      } else if (this.search.search) {
        this.customObject[this.search.type] = this.search.search;
      }

      this.requestMadeByAnUser = true;
      this.$router.push({ query: this.customObject }).catch(() => {
        this.doingSearch = false;
      });
    },

    updateDisabledDates(status) {
      if (status === "Expiring") {
        this.disabledDates = { to: new Date() };
        this.disableType = "to";
      } else {
        this.disabledDates = { from: new Date() };
        this.disableType = "from";
      }
    },
    async withdrawCertification(row) {
      if (await this.submitGuard(this.$wording.home.withdrawSubmission)) {
        let response = false;
        if (row.certificationStatus === "Draft") {
          response = await this.$store.dispatch(
            "properties/deleteSubmission",
            row.id
          );
        } else {
          response = await this.$store.dispatch(
            "properties/deleteSubmissionAdmin",
            row.id
          );
        }
        if (response) {
          this.$refs["dashboard-table"].getData();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.results-table-wrapper {
  position: relative;
  min-height: 160px;
}

.table-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: flex-start;
}

.searching-row {
  width: 100%;
  border: 1px solid #d7dce2;
  background-color: #ffffff;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #2f3f4f;
}
</style>
